// ─── Site Content Manager — axios wrappers ───────────────────────────────────
//
// Thin facade over the existing axios client. Centralizes admin endpoint paths
// so PagesEditor / NavigationEditor / SeoEditor don't each carry literal URLs
// (and we can swap the legacy /api/admin/pages path for /api/admin/site-pages
// here only if the backend renames the mount point).
//
// All endpoints require a super-admin session — gating lives in the backend
// `isSuper` middleware on adminRoutes.ts. The frontend AdminApp shell already
// bounces non-supers via the AuthContext guard.
//
// Backend endpoint map (current as of fix/mon-bug-fixes):
//   GET    /api/admin/pages                  → listPages
//   PUT    /api/admin/pages/:slug            → savePage (full SEO + contentBlocks)
//   PATCH  /api/admin/pages/:slug/status     → setPageStatus { published }
//   GET    /api/admin/site-navigation        → getAdminNavigation
//   PUT    /api/admin/site-navigation        → putAdminNavigation (per-locale)
//   GET    /api/admin/site-settings          → getAdmin
//   PUT    /api/admin/site-settings          → putAdmin
//
// NOTE: the spec docs call the page endpoint /api/admin/site-pages — the
// real route in adminRoutes.ts is /api/admin/pages. We use the actual path
// here; if the backend ever renames it, swap the constants below.

import api from "../api";

const PAGES_BASE        = "/api/admin/pages";
const NAVIGATION_BASE   = "/api/admin/site-navigation";
const SETTINGS_BASE     = "/api/admin/site-settings";

// ── Pages ────────────────────────────────────────────────────────────────────

export const listPages = async () => {
  const { data } = await api.get(PAGES_BASE);
  return data;
};

export const savePage = async (slug, payload) => {
  // payload: { translations: { he, en, ru, ar, es }, hreflangMap? }
  const { data } = await api.put(`${PAGES_BASE}/${encodeURIComponent(slug)}`, payload);
  return data;
};

export const setPageStatus = async (slug, published) => {
  const { data } = await api.patch(
    `${PAGES_BASE}/${encodeURIComponent(slug)}/status`,
    { published: Boolean(published) }
  );
  return data;
};

// ── Navigation + Footer ──────────────────────────────────────────────────────

export const getNavigation = async () => {
  const { data } = await api.get(NAVIGATION_BASE);
  return data;
};

export const saveNavigation = async ({ locale, navigation, footer, branding }) => {
  if (!locale) throw new Error("saveNavigation: locale is required");
  const body = { locale };
  if (navigation !== undefined) body.navigation = navigation;
  if (footer !== undefined) body.footer = footer;
  if (branding !== undefined) body.branding = branding;
  const { data } = await api.put(NAVIGATION_BASE, body);
  return data;
};

// ── Site-wide settings (favicon, manifest, JSON-LD globals) ──────────────────

export const getSettings = async () => {
  const { data } = await api.get(SETTINGS_BASE);
  return data;
};

export const saveSettings = async (patch) => {
  // patch is a partial body; only provided fields are touched on the backend.
  const { data } = await api.put(SETTINGS_BASE, patch);
  return data;
};

export default {
  listPages,
  savePage,
  setPageStatus,
  getNavigation,
  saveNavigation,
  getSettings,
  saveSettings,
};
